<script setup lang="ts">
import FilterListContent from '#components/tag/filterTag/FilterListContent.vue';
import type { FilterTagVariantType } from '#components/tag/filterTag/FilterTag.model';
import FilterTag from '#components/tag/filterTag/FilterTag.vue';
import { useKpisPerimeter, type FilterNameType } from '#composables/kpisPerimeter';
import { useLanguage } from '#composables/useLanguage';
import { getCheckedItems } from '#domain/core/filter/filter.utilities';
import type { PerimeterFilterViewType, ProductFilterConfigType } from '@carrefour-gcs/shared';
import { computed, ref } from 'vue';
import { usePerimeterFiltersData } from './composables';
import { useSelectedTreeviewItemLabels } from './composables/useSelectedTreeviewItemLabels';

const {
  variant = 'default',
  size = 'lg',
  productFilterConfig,
  perimeterFilterView = undefined,
  minVisibleItems = 1,
  showMoreEnabled = false
} = defineProps<{
  variant?: FilterTagVariantType;
  size?: 'sm' | 'lg';
  productFilterConfig: ProductFilterConfigType;
  perimeterFilterView?: PerimeterFilterViewType;
  minVisibleItems?: number;
  showMoreEnabled?: boolean;
}>();

const { t } = useLanguage();

const isExpanded = ref(false);

const {
  taxonomy: { taxonomy },
  filters: { selectedFiltersTree }
} = useKpisPerimeter();

const globalSearch = ref('');

const isEnabledPredicate = (filterName: FilterNameType) => {
  if (
    [
      'activityTypesFilters',
      'nomenclatureFilters',
      'storesFilters',
      'storesRegionFilters',
      'integratedFranchisedFilters'
    ].includes(filterName)
  ) {
    return true;
  }

  const checkedFilters = getCheckedItems(selectedFiltersTree.value[filterName] || {});
  return !!Object.keys(checkedFilters).length;
};

const filters = usePerimeterFiltersData({
  perimeterFilterView,
  checkedItemsState: selectedFiltersTree,
  debouncedSearch: globalSearch,
  productFilterConfig,
  taxonomy,
  isEnabledPredicate
});

const isLoading = computed(() => Object.values(filters.value).some(({ isLoading }) => isLoading.value));

const allAppliedFilters = computed(() =>
  Object.values(filters.value).filter((filter) => useSelectedTreeviewItemLabels(filter.items).value.length > 0)
);

const visibleFilters = computed(() => {
  if (!showMoreEnabled || isExpanded.value) {
    return allAppliedFilters.value;
  }
  return allAppliedFilters.value.slice(0, minVisibleItems);
});

const hiddenItemsCount = computed(() => allAppliedFilters.value.length - minVisibleItems);
</script>

<template>
  <div v-if="isLoading" />
  <template v-else-if="!isLoading">
    <template v-for="filter in visibleFilters" :key="filter.label.value">
      <FilterTag :title="filter.label.value" :variant="variant" :size="size">
        <FilterListContent :items="useSelectedTreeviewItemLabels(filter.items).value" :visible-item-count="1" />
      </FilterTag>
    </template>
    <button
      v-if="showMoreEnabled && allAppliedFilters.length > minVisibleItems"
      class="text-sm font-bold text-blue-600 cursor-pointer underline"
      @click="isExpanded = !isExpanded"
    >
      {{ isExpanded ? t('commonLabel.seeLess') : `${t('commonLabel.seeMore')} (${hiddenItemsCount})` }}
    </button>
  </template>
</template>
